import { S, save, _IMPORT_KEYS, _sanitizeImportArray, CURRENT_SCHEMA, RESTORE_KEYS, runMigrations, restampRestoredRows, flushCloudNow, stripNeverRestored, schemaLegibility } from '../core/state.js';
import { stripSecrets, preserveSecrets } from '../core/secrets.js';
import { esc, f, fU, fByCur, ds, bc, toUSD, realizedUSD, pc, ml } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { PS, setupPoll, refreshPrices, tdRequest } from '../core/prices.js';
import { idbSave, idbList, idbRestore, idbClearState, markStateRestored } from '../core/persistence.js';
import { lg } from '../modules/history.js';
import { getMs, hMoTRY, hTotTRY, hTotUSD, tdComputeGross } from '../core/math.js';
import { _sbHeaders, _sbReady, _sbUser, _isOnline, SB_REST } from '../core/auth.js';
import { renderAll } from '../app/render-loop.js';
import { testAIConnection, getProviderDefaults } from '../core/ai.js';

// === API KEY TESTING ===
export async function testTdKey() {
  const key = document.getElementById('sTdKey').value.trim();
  const el = document.getElementById('tdKeyStatus');
  const btn = document.getElementById('tdTestBtn');
  if (!key) {
    el.innerHTML = '<span style="color:var(--red)">Enter a key first</span>';
    return;
  }
  btn.textContent = 'Testing...';
  btn.disabled = true;
  try {
    // ⚠ THE SAME TRANSPORT THE FEED USES (SEC-005). This was a bare `fetch` with the key in the
    // query string while `prices.js` now tries a header first. A Test button that exercises a
    // different path than the thing it is testing reports on itself: it would go green while the
    // real feed failed its preflight, which is the one failure mode this change can introduce.
    const r = await tdRequest('https://api.twelvedata.com/quote?symbol=NVDA', key, { timeout: 15000, retries: 0 });
    const d = await r.json();
    if (d.code === 401 || d.code === 403) {
      el.innerHTML = '<span style="color:var(--red)">Invalid key — check twelvedata.com</span>';
    } else if (d.close) {
      el.innerHTML = `<span style="color:var(--grn)">✓ Working — NVDA: $${d.close} (${d.percent_change > 0 ? '+' : ''}${d.percent_change}%)</span>`;
    } else {
      el.innerHTML = `<span style="color:var(--yel)">Response received but unexpected format</span>`;
    }
  } catch (e) {
    el.innerHTML = `<span style="color:var(--red)">Connection failed: ${esc(e.message)}</span>`;
  }
  btn.textContent = 'Test';
  btn.disabled = false;
}

export async function testBistProxy() {
  const url = document.getElementById('sBistProxy').value.trim();
  const el = document.getElementById('bistProxyStatus');
  const btn = document.getElementById('bistTestBtn');
  if (!url) {
    el.innerHTML = '<span style="color:var(--red)">Enter the Google Apps Script URL first</span>';
    return;
  }
  btn.textContent = 'Testing...';
  btn.disabled = true;
  try {
    const r = await fetch(url + '?symbols=THYAO.IS', { signal: AbortSignal.timeout(15000) });
    const d = await r.json();
    if (d.THYAO && d.THYAO.price) {
      el.innerHTML = `<span style="color:var(--grn)">✓ Working — THYAO: ₺${d.THYAO.price} (${d.THYAO.changePct > 0 ? '+' : ''}${d.THYAO.changePct.toFixed(2)}%)</span>`;
    } else if (d.error) {
      el.innerHTML = `<span style="color:var(--red)">Proxy error: ${esc(d.error)}</span>`;
    } else {
      el.innerHTML = `<span style="color:var(--yel)">Response received but unexpected format: ${esc(JSON.stringify(d).substring(0, 100))}</span>`;
    }
  } catch (e) {
    el.innerHTML = `<span style="color:var(--red)">Connection failed: ${esc(e.message)}</span>`;
  }
  btn.textContent = 'Test';
  btn.disabled = false;
}

// === FX RATES ===
export function saveFxRates() {
  const usd = parseFloat(document.getElementById('sFxUsd').value);
  const eur = parseFloat(document.getElementById('sFxEur').value);
  const xau = parseFloat(document.getElementById('sFxXau').value);
  const xag = parseFloat(document.getElementById('sFxXag').value);
  // F-005: Bounds validation for FX rates
  if (usd && (usd < 0.01 || usd > 200)) {
    toast('USD/TRY rate out of range (0.01-200)', 'a-r');
    return;
  }
  if (eur && (eur < 0.01 || eur > 200)) {
    toast('EUR/TRY rate out of range (0.01-200)', 'a-r');
    return;
  }
  if (xau && (xau < 0.01 || xau > 100000)) {
    toast('XAU price out of range', 'a-r');
    return;
  }
  if (xag && (xag < 0.01 || xag > 10000)) {
    toast('XAG price out of range', 'a-r');
    return;
  }
  let n = 0;
  if (usd > 0) {
    S.fxRate = usd;
    S.prices['USD'] = { price: usd, changePct: 0, src: 'manual', ts: Date.now(), cur: 'TRY' };
    n++;
  }
  if (eur > 0) {
    S.fxRateEUR = eur;
    S.prices['EUR'] = { price: eur, changePct: 0, src: 'manual', ts: Date.now(), cur: 'TRY' };
    n++;
  }
  if (xau > 0) {
    S.prices['XAU'] = { price: xau, changePct: 0, src: 'manual', ts: Date.now(), cur: 'TRY' };
    n++;
  }
  if (xag > 0) {
    S.prices['XAG'] = { price: xag, changePct: 0, src: 'manual', ts: Date.now(), cur: 'TRY' };
    n++;
  }
  document.getElementById('fxUSD').textContent = S.fxRate ? f(S.fxRate, 2) : '—';
  document.getElementById('fxEUR').textContent = S.fxRateEUR ? f(S.fxRateEUR, 2) : '—';
  if (S.prices['XAU']) document.getElementById('fxXAU').textContent = f(S.prices['XAU'].price, 2);
  lg('fx', '—', 'Manual FX: ' + n + ' rates updated');
  save();
  renderAll();
  toast(n + ' FX rates saved', 'a-b');
}

// === SETTINGS SAVE ===
export function saveSett() {
  S.base = parseFloat(document.getElementById('sBase').value) || 2400;
  S.settings.eqInt = parseInt(document.getElementById('sEq').value) || 120;
  S.settings.crInt = parseInt(document.getElementById('sCr').value) || 60;
  S.settings.notif = document.getElementById('sNot').value === '1';
  S.settings.tdApiKey = document.getElementById('sTdKey').value.trim();
  S.settings.bistProxyUrl = document.getElementById('sBistProxy').value.trim();
  // An eyes-on confirmation the app cannot compute. Absent means NOT armed, never armed.
  S.settings.cycleXrpConfirmed = document.getElementById('sCycleXrp')?.checked === true;
  S.settings.mongoDb = (document.getElementById('sMongoDb')?.value || '').trim();
  S.settings.mongoColl = (document.getElementById('sMongoColl')?.value || '').trim();
  S.settings.backupDays = parseInt(document.getElementById('sBkDays').value) || 7;
  S.settings.rebalThreshold = parseFloat(document.getElementById('sRebalTh').value) || 5;
  { // Reserve target: blank = auto-derive (6× forward essential outflow); a value = manual override
    const rv = parseFloat(document.getElementById('sResTgt').value);
    if (rv > 0) { S.settings.reserveTargetUSD = rv; S.settings.reserveTargetManual = true; }
    else { S.settings.reserveTargetManual = false; }
  }
  S.settings.riskBudgetUSD = parseFloat(document.getElementById('sRiskBgt')?.value) || 0; // 0 = auto (5% of net worth)
  {
    // The month the owner says he entered positions that predate the app. DECLARED, never
    // inferred: everything computed from it is labelled as such, and clearing it makes those
    // positions uncomputable again rather than silently dating them to something else.
    const bm = (document.getElementById('sBasisMonth')?.value || '').trim();
    S.settings.basisMonth = /^\d{4}-\d{2}$/.test(bm) ? bm : null;
  }
  S.settings.trInflation = parseFloat(document.getElementById('sInfl').value) / 100 || 0;
  save();
  setupPoll();
  toast('Saved — refreshing prices...', 'a-b');
  refreshPrices();
}

// === NOTIFICATION PERMISSIONS ===
export function requestNotifPerm() {
  if (!('Notification' in window)) {
    toast('Browser does not support notifications', 'a-r');
    return;
  }
  Notification.requestPermission().then(p => {
    updateNotifPermUI();
    if (p === 'granted') {
      toast('Notifications enabled', 'a-g');
      document.getElementById('sNot').value = '1';
    } else if (p === 'denied') {
      toast('Notifications blocked — check browser settings', 'a-r');
    }
  });
}

export function updateNotifPermUI() {
  const btn = document.getElementById('notifPermBtn');
  const st = document.getElementById('notifPermStatus');
  if (!btn || !st) return;
  if (!('Notification' in window)) {
    btn.style.display = 'none';
    st.innerHTML = '<span style="color:var(--red)">Not supported in this browser</span>';
    return;
  }
  const p = Notification.permission;
  if (p === 'granted') {
    btn.style.display = 'none';
    st.innerHTML = '<span style="color:var(--grn)">✓ Permitted</span>';
  } else if (p === 'denied') {
    btn.style.display = 'none';
    st.innerHTML = '<span style="color:var(--red)">✗ Blocked — reset in browser site settings</span>';
  } else {
    btn.style.display = '';
    st.innerHTML = '<span style="color:var(--yel)">Not yet granted</span>';
  }
}

// === ANNUAL TAX SUMMARY ===
/**
 * Was this daily time deposit accruing at any point during the tax year `yr`?
 *
 * Replaces `startDate.startsWith(yr)`, which asked whether it STARTED in the year — a question
 * whose answer is "no" for every year after the first, on a deposit that never stops accruing.
 * (TAX-003)
 */
export function _dailyTdTouchesYear(t, yr) {
  const td = (t && t.td) || {};
  if (!td.startDate) return false;
  const y = parseInt(yr, 10);
  if (!isFinite(y)) return false;
  const start = new Date(td.startDate).getTime();
  const yearStart = new Date(y, 0, 1).getTime();
  const yearEnd = new Date(y + 1, 0, 1).getTime();   // exclusive, see the tax branch
  if (!isFinite(start) || start >= yearEnd) return false;         // opened after the year ended
  if (t.status === 'Closed') {
    // A deposit closed before the year began contributed nothing to it. With no close date
    // recorded the deposit is treated as still relevant rather than silently dropped.
    if (!t.closeDate) return true;
    const closed = new Date(t.closeDate).getTime();
    if (isFinite(closed) && closed < yearStart) return false;
  }
  return true;
}

/**
 * Gross interest a daily deposit accrued DURING tax year `yr`, in its own currency.
 *
 *     year's interest = gross(min(end, 1 Jan of yr+1)) − gross(1 Jan of yr)
 *
 * Exported for the same reason as the gate above: a test that re-implements this cannot fail
 * when this changes, and that has now happened three times in one day (PROC-013). (TAX-003)
 *
 * @param {Object} t     the trade
 * @param {string} yr    the tax year, e.g. '2027'
 * @param {Date} [asOf]  "now", injected so the answer is not a function of the wall clock
 */
export function _dailyTdYearGross(t, yr, asOf) {
  const y = parseInt(yr, 10);
  if (!t || !t.td || !isFinite(y)) return 0;
  const yearStart = new Date(y, 0, 1);
  // ⚠ THE YEAR'S END IS THE NEXT YEAR'S START, EXCLUSIVE — or a day falls between two slices
  // and is taxed in neither. Caught by asserting that 2026's slice plus 2027's equals the whole
  // life: they came up **₺289 short**, exactly 31 December's interest on a ₺245k balance at
  // 40 %. `23:59:59` looks equivalent and is not — the segment walk floors to whole days, so
  // that instant is still inside the 30th.
  const yearEnd = new Date(y + 1, 0, 1);
  const nowD = asOf ? new Date(asOf) : new Date();
  // Open deposits accrue only to today — never book unearned future interest as taxable.
  const endDate =
    t.status === 'Closed'
      ? (t.closeDate
        ? new Date(Math.min(new Date(t.closeDate).getTime(), yearEnd.getTime()))
        : yearEnd)
      : (nowD < yearEnd ? nowD : yearEnd);
  const cEnd = tdComputeGross(t.td, endDate);
  const cStart = tdComputeGross(t.td, yearStart);
  return Math.max(0, (cEnd ? cEnd.grossInt : 0) - (cStart ? cStart.grossInt : 0));
}

export function renderTaxSummary() {
  // Populate year selector
  const sel = document.getElementById('taxYear');
  const years = new Set();
  S.realized.forEach(r => {
    if (r.date) years.add(r.date.slice(0, 4));
  });
  S.trades.filter(t => t.type === 'TimeDeposit').forEach(t => {
    if (t.td?.startDate) years.add(t.td.startDate.slice(0, 4));
  });
  const curYear = new Date().getFullYear().toString();
  years.add(curYear);
  const sortedYears = [...years].sort().reverse();
  if (!sel.options.length || sel.options.length !== sortedYears.length) {
    sel.innerHTML = sortedYears
      .map(y => `<option value="${y}" ${y === curYear ? 'selected' : ''}>${y}</option>`)
      .join('');
  }
  const yr = sel.value || curYear;

  const rows = [];
  let totalGross = 0,
    totalWHT = 0,
    totalNet = 0;

  // 1. Realized trade P&L (capital gains)
  const yrRealized = S.realized.filter(r => r.date && r.date.startsWith(yr));
  const byType = {};
  yrRealized.forEach(r => {
    const t = S.trades.find(x => x.ticker === r.ticker);
    const type = t?.type || 'Other';
    if (!byType[type]) byType[type] = { gross: 0, count: 0 };
    const pnlUSD = realizedUSD(r);
    byType[type].gross += pnlUSD;
    byType[type].count++;
  });
  Object.entries(byType).forEach(([type, d]) => {
    rows.push({
      cat: 'Capital Gains',
      src: type + ' (' + d.count + ' trades)',
      gross: d.gross,
      wht: 0,
      net: d.gross,
      note: 'No WHT on Türkiye equities held >1yr'
    });
    totalGross += d.gross;
    totalNet += d.gross;
  });

  // 2. Time Deposit interest (matured in year)
  const tdTrades = S.trades.filter(t => t.type === 'TimeDeposit');
  let tdGrossInt = 0,
    tdWHT = 0,
    tdNetInt = 0;
  tdTrades.forEach(t => {
    if (!t.td) return;
    const cur = t.currency || 'TRY';
    // Check if matured in this year (for monthly TDs) or accruing (for daily)
    if (t.td.kind === 'daily' && _dailyTdTouchesYear(t, yr)) {
      // Daily TD: accrued interest so far.
      //
      // ⚠ THE RATE IS NOT ONE NUMBER. `changeTdRate` records every change in `td.rateSegments`,
      // and this branch used the flat `td.annualRate` — which is the CURRENT rate — over the
      // whole period. All three of his deposits stepped DOWN (43→42, 42→40, 42→40), so the
      // summary reported the lowest rate for the whole life and understated the tax base:
      //
      //   2026-04-08 (closed)  ₺22,750 → ₺23,099      2026-04-30 (open)  ₺24,940 → ₺26,177
      //   2026-05-22 (open)    ₺35,418 → ₺37,123      total  ₺83,108 → ₺86,399
      //
      // **₺3,292 of gross deposit interest missing from the 2026 return.** (TAX-002)
      //
      // `tdComputeGross` already walks the segments — it is what the Reserve, Dashboard and
      // Goals pages read — and it now takes the date to stop at, so the tax year's own cut-off
      // governs instead of "today". One definition, asked a different question (rule #24).
      //
      // ⚠ TAX-003 — AND THIS BRANCH USED TO ASK THE WRONG QUESTION ENTIRELY.
      //
      // The gate was `t.td.startDate?.startsWith(yr)`: *did this deposit START in the year being
      // reported.* A daily deposit does not end when the year does — it goes on accruing — so
      // from **1 January 2027** his two open deposits, **₺563,576 of principal**, would have
      // reported **zero interest, forever**, in every return after the one they were opened in.
      // Silently: nothing is missing, the row simply is not there.
      //
      // The right question is *did it accrue DURING the year*, and the right amount is that
      // year's SLICE — not its whole life. Both are computable now only because
      // `tdComputeGross` takes a cut-off date (MATH-001, fixed this morning):
      //
      //     year's interest = gross(min(end, 31 Dec)) − gross(1 Jan)
      //
      // For 2026 the opening term is 0 — the deposits started inside it — so this reproduces
      // today's figures exactly, which is the property the tests pin. For 2027 it yields the
      // 2027 slice instead of nothing.
      // ⚠ THE YEAR'S END IS THE NEXT YEAR'S START, EXCLUSIVE — or a day falls between the two
      // slices and is taxed in neither. Caught by asserting that 2026's slice plus 2027's must
      // equal the whole life: they came up **₺289 short**, which is exactly 31 December's
      // interest on a ₺245k balance at 40 %. Using 23:59:59 looks equivalent and is not: the
      // segment walk floors to whole days, so that instant is still inside 30 December.
      const gross = _dailyTdYearGross(t, yr);
      const wht = (gross * (t.td.withholdingPct || t.td.withholding || 10)) / 100;
      const grossUSD = toUSD(gross, cur);
      const whtUSD = toUSD(wht, cur);
      tdGrossInt += grossUSD;
      tdWHT += whtUSD;
      tdNetInt += grossUSD - whtUSD;
    } else if (t.td.kind === 'monthly' && t.td.startDate) {
      // Monthly TD: maturity in year?
      const matDate = new Date(t.td.startDate);
      matDate.setMonth(matDate.getMonth() + (t.td.termMonths || 0));
      if (
        matDate.getFullYear().toString() === yr ||
        (t.td.confirmed && t.closeDate?.startsWith(yr))
      ) {
        const p = t.td.principal || 0;
        const rate = (t.td.annualRate || 0) / 100;
        const months = t.td.termMonths || 0;
        const gross = (p * rate * months) / 12;
        const wht = (gross * (t.td.withholdingPct || t.td.withholding || 10)) / 100;
        const grossUSD = toUSD(gross, cur);
        const whtUSD = toUSD(wht, cur);
        tdGrossInt += grossUSD;
        tdWHT += whtUSD;
        tdNetInt += grossUSD - whtUSD;
      }
    }
  });
  if (tdGrossInt > 0) {
    rows.push({
      cat: 'Deposit Interest',
      src: 'Time Deposits',
      gross: tdGrossInt,
      wht: tdWHT,
      net: tdNetInt,
      note: 'WHT applied at source'
    });
    totalGross += tdGrossInt;
    totalWHT += tdWHT;
    totalNet += tdNetInt;
  }

  // 3. Dividend income — DRIP (reinvested) dividends are excluded from the taxable
  // total by user policy: not taken as cash, so not surfaced as realized income here.
  const yrDivs = (S.dividends || []).filter(d => d.date && d.date.startsWith(yr) && !d.drip);
  if (yrDivs.length > 0) {
    let divGross = 0,
      divWht = 0,
      divNet = 0;
    yrDivs.forEach(d => {
      divGross += toUSD(d.gross, d.currency || 'USD');
      divWht += toUSD(d.whtAmt || 0, d.currency || 'USD');
      divNet += toUSD(d.net || 0, d.currency || 'USD');
    });
    rows.push({
      cat: 'Dividends',
      src: yrDivs.length + ' payment(s)',
      gross: divGross,
      wht: divWht,
      net: divNet,
      note: 'WHT varies by jurisdiction'
    });
    totalGross += divGross;
    totalWHT += divWht;
    totalNet += divNet;
  }

  // 4. Salary / Other income — actual YTD realized from cashflow (no forward projection)
  (function() {
    let ytdTRY = 0;
    Object.keys(S.cashflow || {}).forEach(k => {
      if (!k.startsWith(yr + '-')) return;
      ytdTRY += S.cashflow[k].income || 0;
    });
    // Tax summary reports REALIZED income only — projected/unearned months are
    // excluded from the taxable total (they belong in the cashflow/goals forecast).
    const ytdUSD = toUSD(ytdTRY, 'TRY');
    if (ytdUSD > 0) {
      rows.push({
        cat: 'Employment/Other Income',
        src: 'YTD actual (realized to date)',
        gross: ytdUSD,
        wht: 0,
        net: ytdUSD,
        note: 'Pre-tax, realized this year'
      });
      totalGross += ytdUSD;
      totalNet += ytdUSD;
    }
  })();

  // Stats
  const statsEl = document.getElementById('taxStats');
  if (statsEl)
    statsEl.innerHTML = `
    <div class="gls-cell"><div class="l">YEAR</div><div class="v cy">${yr}</div></div>
    <div class="gls-cell"><div class="l">GROSS INCOME</div><div class="v gold">${fU(totalGross)}</div></div>
    <div class="gls-cell"><div class="l">WHT DEDUCTED</div><div class="v neg">${fU(-totalWHT)}</div></div>
    <div class="gls-cell"><div class="l">NET (POST-WHT)</div><div class="v ${totalNet >= 0 ? 'pos' : 'neg'}">${fU(totalNet)}</div></div>
    <div class="gls-cell"><div class="l">REALIZED TRADES</div><div class="v">${yrRealized.length}</div></div>`;

  // Table
  // ⚠ NOT `taxBody`. That id belongs to the TAX PAGE (`#page-tax > #taxNeo > div#taxBody`),
  // and it appeared TWICE in index.html — the only duplicate id in the file.
  // `getElementById` returns the first in document order, which is the Tax page's container at
  // line 938, not this table at line 1405. So every call to `renderTaxSummary` — and `initSett`
  // calls it on every Settings open — wrote its `<tr>` rows into the Tax page and destroyed
  // whatever `tax.js` had rendered there.
  //
  // Reproduced live 2026-08-15: one call took the Tax page from 13,092 characters to 805 and
  // removed the `tax-vband` band reading *"✓ IN CASH · Booked as one-time income 2026-08-10 ·
  // ₺93,669.31"*. The rows also arrived as `<tr>` inside a `<div>`, so the parser dropped the
  // row and cell wrappers and the result rendered as run-on text. Meanwhile this table — the
  // one the code was aiming at — stayed empty, always. (TAX-001)
  const bodyEl = document.getElementById('setTaxBody');
  if (bodyEl) {
    if (!rows.length) {
      bodyEl.innerHTML = `<tr><td colspan="6" class="neo-empty">No taxable events for ${yr}</td></tr>`;
      return;
    }
    bodyEl.innerHTML =
      rows
        .map(
          r =>
            `<tr><td><strong>${r.cat}</strong></td><td style="font-size:11px">${r.src}</td>
      <td class="tr">${fU(r.gross)}</td><td class="tr" style="color:var(--red)">${r.wht > 0 ? fU(-r.wht) : '—'}</td>
      <td class="tr ${pc(r.net)}" style="font-weight:600">${fU(r.net)}</td>
      <td style="font-size:10px;color:var(--t3)">${esc(r.note)}</td></tr>`
        )
        .join('') +
      `<tr style="border-top:2px solid var(--bd);font-weight:700">
      <td colspan="2">TOTAL</td><td class="tr">${fU(totalGross)}</td>
      <td class="tr" style="color:var(--red)">${totalWHT > 0 ? fU(-totalWHT) : '—'}</td>
      <td class="tr ${pc(totalNet)}">${fU(totalNet)}</td><td></td></tr>`;
  }
}

// === SETTINGS INITIALIZATION ===
export function initSett() {
  document.getElementById('sBase').value = S.base;
  document.getElementById('sTdKey').value = S.settings.tdApiKey || '';
  document.getElementById('sBistProxy').value = S.settings.bistProxyUrl || '';
  { const _x = document.getElementById('sCycleXrp'); if (_x) _x.checked = S.settings.cycleXrpConfirmed === true; }
  { const e1 = document.getElementById('sMongoDb'); if (e1) e1.value = S.settings.mongoDb || ''; }
  { const e2 = document.getElementById('sMongoColl'); if (e2) e2.value = S.settings.mongoColl || ''; }
  { const e = document.getElementById('sResTgt'); if (e) e.value = S.settings.reserveTargetManual ? (S.settings.reserveTargetUSD || '') : ''; }
  { const e = document.getElementById('sRiskBgt'); if (e) e.value = S.settings.riskBudgetUSD > 0 ? S.settings.riskBudgetUSD : ''; }
  document.getElementById('sEq').value = S.settings.eqInt || 120;
  document.getElementById('sCr').value = S.settings.crInt || 60;
  document.getElementById('sNot').value = S.settings.notif ? '1' : '0';
  document.getElementById('sBkDays').value = S.settings.backupDays || 7;
  document.getElementById('sRebalTh').value = S.settings.rebalThreshold || 5;
  { const el = document.getElementById('sBasisMonth'); if (el) el.value = S.settings.basisMonth || ''; }
  document.getElementById('sInfl').value = ((S.settings.trInflation || 0) * 100).toFixed(1);
  // FX rates
  document.getElementById('sFxUsd').value = S.fxRate || '';
  document.getElementById('sFxEur').value = S.fxRateEUR || '';
  document.getElementById('sFxXau').value = S.prices['XAU'] ? S.prices['XAU'].price : '';
  document.getElementById('sFxXag').value = S.prices['XAG'] ? S.prices['XAG'].price : '';
  // Last backup
  const lb = S.lastBackup;
  document.getElementById('sLastBk').textContent = lb ? ds(lb) : 'Never';
  if (lb) {
    const d = Math.floor((Date.now() - lb) / 86400000);
    document.getElementById('sLastBkAgo').textContent = d > 0 ? d + ' days ago' : 'Today';
  }
  // AI settings
  initAISettings();
  // Push notification settings
  if (typeof window.initPushSettings === 'function') window.initPushSettings();
  // Auto-backup list
  loadBackupList();
  // Quick price table
  renderQP();
  updateNotifPermUI();
  renderTaxSummary();
}

// === AUTO-BACKUP LIST & RESTORE ===
export async function loadBackupList() {
  const el = document.getElementById('idbList');
  if (!el) return;
  try {
    const list = await idbList();
    if (!list.length) {
      el.innerHTML =
        '<span style="color:var(--t3)">No auto-backups yet. They start accumulating after your next save.</span>';
      return;
    }
    el.innerHTML =
      '<table class="tbl" style="width:100%;font-size:12px"><thead><tr><th style="text-align:left">Time</th><th style="text-align:left">Holdings</th><th style="text-align:left">Trades</th><th>Act</th></tr></thead><tbody>' +
      list
        .map((b, i) => {
          const d = new Date(b.ts);
          const timeStr = d.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          const data = JSON.parse(b.data);
          const hCount = data.holdings?.length || 0;
          const tCount = data.trades?.length || 0;
          const isLatest = i === 0;
          return `<tr><td class="txt" style="color:var(--t1)">${timeStr}${isLatest ? ' <span style="color:var(--grn);font-size:10px">latest</span>' : ''}</td><td class="txt" style="color:var(--t2)">${hCount}</td><td class="txt" style="color:var(--t2)">${tCount}</td><td><button class="glassbtn" style="font-size:10px;padding:2px 10px" data-action="restoreBackup" data-args='[${b.id}]'>Restore</button></td></tr>`;
        })
        .join('') +
      '</tbody></table>';
  } catch (e) {
    el.innerHTML = '<span style="color:var(--red)">IndexedDB error: ' + e + '</span>';
  }
}

export async function restoreBackup(id) {
  // The dialog says what a restore can and cannot do. It cannot resurrect a deleted row: a
  // deletion leaves a tombstone, `mergeStates` takes the UNION of this device's tombstones and
  // the cloud's, and `mergeArrays` drops anything in that union regardless of any timestamp.
  // Saying so is the honest fix; changing the merge to let a file un-delete rows would be a
  // worse defect than the one it closes. (DATA-011, 2026-08-16)
  if (!await uiConfirm('Restore this backup? Current data will be overwritten. Rows deleted since the backup was taken cannot be brought back.', { title: 'Restore backup', okText: 'Restore', danger: true })) return;
  try {
    const data = await idbRestore(id);
    if (!data) {
      toast('Backup not found', 'a-r');
      return;
    }
    // DATA-014. THE SAME LEGIBILITY RULE AS `importData`, AND FOR A DIFFERENT REASON.
    //
    // There, a low version is a fact to be consumed. Here it is too — that is why this door lets
    // the number go DOWN. But a blob carrying NO version cannot lower anything: `Object.assign`
    // does not overwrite an absent key, so `runMigrations` reads the LIVE version, finds nothing
    // pending, and installs pre-migration data into this build having run zero of them. Four
    // full-state exports on disk (2026-03-29 … 04-07) have no `_schemaVersion`, so the blob class
    // is real; the snapshot store holds ten entries and gains one only on an import, so an
    // old one survives there until ten later imports push it out.
    //
    // ⚠ Declining is not a retreat to the allowlist (CONTRACT-005). Trust is about authorship,
    // not legibility, and this changes nothing about what a legible blob installs.
    const legibility = schemaLegibility(data);
    if (!legibility.ok) {
      toast(legibility.why === 'newer'
        ? 'This snapshot is from a newer version (v' + legibility.v + '). Update the app first.'
        : 'This snapshot predates schema versioning and cannot be migrated — restore a dated export instead.', 'a-r');
      return;
    }
    // SEC-001's third door: a wholesale install must not delete the key configured on this
    // device, exactly as an import did before `preserveSecrets` was wired there.
    //
    // ⚠ THIS COMMENT NAMED THE WRONG WRITER until DATA-014 measured it. `doBackup` produces the
    // DOWNLOADED FILE — stripped, and read by `importData`. What THIS door reads is the
    // IndexedDB snapshot store, whose only writer is `idbSave({ ...S })` one call above the
    // `importData` merge; it strips nothing, because the store never leaves the device. Either
    // way the blob's own keys must lose to this device's, which is what `preserveSecrets` does.
    preserveSecrets(S, data);
    // CONTRACT-005. A backup blob is trusted, so this door stays COMPLETE — it installs keys the
    // `RESTORE_KEYS` allowlist has never heard of, `fxEURUSD` among them. What it must decline are
    // the keys that describe THIS DEVICE rather than the book: `activePortfolioId` (both of his
    // backups carry the SAP XETRA filter, 1 holding of 44), `_scalarTimestamps`, `_truncated`,
    // `lastBackup`, and the three that have no live instance. The rule lives in
    // `stripNeverRestored`, next to the list it reads.
    Object.assign(S, stripNeverRestored(data));
    // A restore is the way out of a blocked device, so it must be able to reach the disk.
    markStateRestored();
    // Same rule as importData, and for the same reason: an auto-backup blob can carry an older
    // `_schemaVersion` (one is written on every import), and this is the more-used recovery
    // button. The floor cannot tell a low version that is TRUE from a store that has not caught
    // up; here it is true. (audit 2026-08-13)
    runMigrations();
    // DATA-011, both halves. Stamp AFTER migrations, so rows a migration touched are covered too.
    restampRestoredRows();
    save();
    // And publish before anything can merge against it. `save()` arms a 15-second debounce; a
    // reload, a 409 version conflict or the 5-minute poll inside that window merges the restored
    // state with a cloud that still holds every row the restore just removed, and the union puts
    // them all back. `flushCloudNow` re-arms the debounce itself when the write does not land.
    flushCloudNow('restore');
    renderAll();
    initSett();
    toast('Restored from auto-backup', 'a-g');
  } catch (e) {
    toast('Restore failed: ' + e, 'a-r');
  }
}

// === QUICK PRICE ENTRY — all instruments ===
let qpFilter = 'all';

export function filterQP(mode) {
  qpFilter = mode;
  renderQP();
}

export function renderQP() {
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    seen.add(h.ticker);
    items.push({ ticker: h.ticker, name: h.name, type: h.type, currency: h.currency, src: 'portfolio', ps: PS[h.ticker] });
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    const k = t.ticker;
    if (!seen.has(k)) {
      seen.add(k);
      items.push({ ticker: k, name: k, type: t.type, currency: t.currency || 'USD', src: 'trade', ps: PS[k] });
    }
  });
  const filtered = items.filter(it => {
    if (qpFilter === 'portfolio') return it.src === 'portfolio';
    if (qpFilter === 'trade') return it.src === 'trade';
    if (qpFilter === 'manual') return (it.ps && it.ps.s === 'm') || !S.prices[it.ticker];
    return true;
  });
  document.getElementById('qpBody').innerHTML = filtered
    .map(it => {
      const p = S.prices[it.ticker];
      const stale = p && Date.now() - p.ts > 86400000;
      const dotCls = !p ? 'dot-manual' : p.src === 'manual' ? 'dot-stale' : stale ? 'dot-stale' : 'dot-live';
      const srcL = !p ? 'Missing' : p.src === 'manual' ? 'Manual' : 'Auto';
      const curP = p ? p.price : '';
      const disp = p ? fByCur(it.currency, p.price) : '—';
      const badge = it.src === 'trade' ? '<span style="font-size:9px;color:var(--yel);margin-left:4px">TRADE</span>' : '';
      return `<tr><td><strong>${esc(it.ticker)}</strong>${badge}<div style="font-size:10px;color:var(--t3)">${esc(it.name || '')}</div></td>
	<td><span class="badge ${bc(it.type)}">${esc(it.type)}</span></td>
	<td><span class="price-src"><span class="dot ${dotCls}"></span>${srcL}</span></td>
	<td>${it.currency}</td>
	<td class="tr">${disp}</td>
	<td class="tr"><input class="price-input" style="width:110px" id="qp_${it.ticker}" value="${curP}" placeholder="${it.currency}"></td></tr>`;
    })
    .join('');
}

export function saveQP() {
  let n = 0;
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    if (!seen.has(h.ticker)) {
      seen.add(h.ticker);
      items.push({ ticker: h.ticker, currency: h.currency });
    }
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    if (!seen.has(t.ticker)) {
      seen.add(t.ticker);
      items.push({ ticker: t.ticker, currency: t.currency || 'USD' });
    }
  });
  items.forEach(it => {
    const el = document.getElementById('qp_' + it.ticker);
    if (el && el.value) {
      const v = parseFloat(el.value);
      if (v > 0) {
        const old = S.prices[it.ticker];
        if (!old || old.price !== v) {
          S.prices[it.ticker] = {
            price: v,
            changePct: old ? ((v - old.price) / old.price) * 100 : 0,
            src: 'manual',
            ts: Date.now(),
            cur: it.currency
          };
          n++;
        }
      }
    }
  });
  if (n > 0) {
    lg('price', '—', 'Quick entry: ' + n + ' prices updated');
    save();
    renderAll();
  }
  toast(n + ' prices updated', 'a-b');
}

// === Floating Quick Price Panel ===
let qpPanelOpen = false,
  qpFFilter = 'all';

export function toggleQPPanel() {
  qpPanelOpen = !qpPanelOpen;
  document.getElementById('qpPanel').style.display = qpPanelOpen ? 'block' : 'none';
  document.getElementById('qpFloat').style.transform = qpPanelOpen ? 'rotate(45deg)' : '';
  if (qpPanelOpen) renderQPF();
}

export function filterQPF(mode) {
  qpFFilter = mode;
  renderQPF();
}

export function renderQPF() {
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    seen.add(h.ticker);
    items.push({ ticker: h.ticker, currency: h.currency, ps: PS[h.ticker] });
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    if (!seen.has(t.ticker)) {
      seen.add(t.ticker);
      items.push({ ticker: t.ticker, currency: t.currency || 'USD', ps: PS[t.ticker] });
    }
  });
  const staleH = 86400000;
  const filtered = items.filter(it => {
    if (qpFFilter === 'manual') return (it.ps && it.ps.s === 'm') || !S.prices[it.ticker];
    if (qpFFilter === 'stale')
      return !S.prices[it.ticker] || Date.now() - (S.prices[it.ticker]?.ts || 0) > staleH;
    return true;
  });
  document.getElementById('qpFBody').innerHTML = filtered
    .map(it => {
      const p = S.prices[it.ticker];
      const curP = p ? p.price : '';
      const disp = p ? fByCur(it.currency, p.price) : '—';
      const stale = p && Date.now() - p.ts > staleH;
      const dotColor = !p ? 'var(--red)' : stale ? 'var(--yel)' : 'var(--grn)';
      return `<tr><td style="padding:4px 6px"><strong style="color:var(--t1)">${esc(it.ticker)}</strong></td>
	<td style="padding:4px 6px;color:var(--t3);font-size:10px">${it.currency}</td>
	<td style="padding:4px 6px;text-align:right;color:var(--t2)"><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${dotColor};margin-right:4px"></span>${disp}</td>
	<td style="padding:4px 6px;text-align:right"><input class="price-input" style="width:90px;font-size:11px" id="qpf_${it.ticker}" value="${curP}" placeholder="${it.currency}"></td></tr>`;
    })
    .join('');
}

export function saveQPF() {
  let n = 0;
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    if (!seen.has(h.ticker)) {
      seen.add(h.ticker);
      items.push({ ticker: h.ticker, currency: h.currency });
    }
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    if (!seen.has(t.ticker)) {
      seen.add(t.ticker);
      items.push({ ticker: t.ticker, currency: t.currency || 'USD' });
    }
  });
  items.forEach(it => {
    const el = document.getElementById('qpf_' + it.ticker);
    if (el && el.value) {
      const v = parseFloat(el.value);
      if (v > 0) {
        const old = S.prices[it.ticker];
        if (!old || old.price !== v) {
          S.prices[it.ticker] = {
            price: v,
            changePct: old ? ((v - old.price) / old.price) * 100 : 0,
            src: 'manual',
            ts: Date.now(),
            cur: it.currency
          };
          n++;
        }
      }
    }
  });
  if (n > 0) {
    lg('price', '—', 'Float QP: ' + n + ' prices updated');
    save();
    renderAll();
  }
  qpPanelOpen = false;
  document.getElementById('qpPanel').style.display = 'none';
  document.getElementById('qpFloat').style.transform = '';
  toast(n + ' prices updated', 'a-b');
}

// === DATA MANAGEMENT ===
export function toggleBistHelp() {
  const el = document.getElementById('bistSetupHelp');
  if (el) el.style.display = el.style.display === 'none' || !el.style.display ? 'block' : 'none';
}

export async function resetAll() {
  if (!await uiConfirm('This will erase ALL data (cloud + local). This cannot be undone.', { title: 'Reset all data', okText: 'Erase everything', danger: true })) return;
  localStorage.removeItem('zagx_v3');
  await idbClearState();
  // Also clear cloud state (direct fetch — SDK has known issues)
  if (_sbReady && _sbUser && _isOnline) {
    try {
      const headers = await _sbHeaders();
      if (headers) {
        await fetch(SB_REST + '/user_state?user_id=eq.' + _sbUser.id, {
          method: 'DELETE', headers
        });
        console.log('[ZAGX] Cloud state deleted');
      }
    } catch (e) { console.warn('[ZAGX] Cloud delete failed:', e); }
  }
  location.reload();
}

export function exportAll() {
  openPBR();
}

export function openPBR() {
  // Collect all unique tickers: holdings + active trades
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    seen.add(h.ticker);
    items.push({ ticker: h.ticker, type: h.type, currency: h.currency, src: 'portfolio' });
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    if (!seen.has(t.ticker)) {
      seen.add(t.ticker);
      items.push({ ticker: t.ticker, type: t.type, currency: t.currency || 'USD', src: 'trade' });
    }
  });
  const rows = items
    .map(it => {
      const p = S.prices[it.ticker];
      const ps = PS[it.ticker];
      const _isM = ps && ps.s === 'm';
      const stale = p && Date.now() - p.ts > 86400000;
      const srcD = !p ? 'dot-manual' : stale ? 'dot-stale' : 'dot-live';
      const srcL = !p ? 'Missing' : p.src === 'manual' ? 'Manual' : 'Auto';
      const curP = p ? p.price : '';
      const disp = p ? fByCur(it.currency, p.price) : '—';
      const badge =
        it.src === 'trade' ? '<span style="font-size:9px;color:var(--yel);margin-left:4px">TRADE</span>' : '';
      return `<tr><td><strong>${esc(it.ticker)}</strong>${badge}</td><td><span class="badge ${bc(it.type)}">${esc(it.type)}</span></td><td>${it.currency}</td>
	<td class="tr">${disp}</td><td><span class="price-src"><span class="dot ${srcD}"></span>${srcL}</span></td>
	<td class="tr"><input class="price-input" style="width:100px" id="pbr_${it.ticker}" value="${curP}" placeholder="${it.currency}"></td></tr>`;
    })
    .join('');
  document.getElementById('pbrB').innerHTML = rows;
  document.getElementById('moPBR').classList.add('active');
}

export function confirmBackup() {
  const items = [];
  const seen = new Set();
  S.holdings.forEach(h => {
    if (!seen.has(h.ticker)) {
      seen.add(h.ticker);
      items.push({ ticker: h.ticker, currency: h.currency });
    }
  });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => {
    if (!seen.has(t.ticker)) {
      seen.add(t.ticker);
      items.push({ ticker: t.ticker, currency: t.currency || 'USD' });
    }
  });
  items.forEach(it => {
    const el = document.getElementById('pbr_' + it.ticker);
    if (el && el.value) {
      const p = parseFloat(el.value);
      if (p > 0) {
        S.prices[it.ticker] = {
          price: p,
          changePct: 0,
          src: 'manual',
          ts: Date.now(),
          cur: it.currency
        };
      }
    }
  });
  save();
  clMo('moPBR');
  doBackup();
}

export function skipBackup() {
  clMo('moPBR');
  doBackup();
}

export function doBackup() {
  S.lastBackup = new Date().toISOString();
  save();
  // THE LEAK. This wrote JSON.stringify(S) — the whole state, API keys included — and that is
  // the file that ended up committed to a repository twenty-six times with two live keys in it.
  // cloudSave and cloudSnapshot had stripped secrets since audit M-10; this third serialiser
  // never got the same treatment. One list, three callers, from now on. (audit 2026-08-03)
  const { state: safe, removed } = stripSecrets(S);
  if (removed.length) console.warn('[ZAGX] backup: ' + removed.length + ' device-local secret(s) withheld — ' + removed.join(', '));
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(safe, null, 2)], { type: 'application/json' }));
  a.download = 'zagx-' + new Date().toISOString().slice(0, 10) + '.json';
  a.click();
  hideBackupBanner();
  updateBackupInfo();
  renderAll();
  toast('Backup saved', 'a-g');
}

// === IMPORT/EXPORT ===
export function importData(e) {
  const file = e.target.files[0];
  if (!file) return;
  const r = new FileReader();
  r.onload = ev => {
    try {
      const parsed = JSON.parse(ev.target.result);
      // F-016: Schema validation before import
      if (!parsed || typeof parsed !== 'object') {
        toast('Invalid file: not a JSON object', 'a-r');
        return;
      }
      if (!Array.isArray(parsed.holdings)) {
        toast('Invalid file: missing holdings array', 'a-r');
        return;
      }
      // ONE RULE, BOTH RESTORE DOORS — `state.schemaLegibility` (DATA-014). These were two inline
      // conditions here and NOTHING AT ALL in `restoreBackup`, which is how the trusted door spent
      // four months adopting whatever version it was handed — including none.
      const legibility = schemaLegibility(parsed);
      if (!legibility.ok) {
        toast(legibility.why === 'newer'
          ? 'Import file is from a newer version (v' + legibility.v + '). Update app first.'
          : 'Invalid file: missing schema version', 'a-r');
        return;
      }
      // CE-11: Deep-sanitize all array items — strip unknown keys to prevent prototype pollution
      Object.keys(_IMPORT_KEYS).forEach(key => {
        if (Array.isArray(parsed[key])) parsed[key] = _sanitizeImportArray(parsed[key], _IMPORT_KEYS[key]);
      });
      // Validate required top-level structures
      if (parsed.cash && typeof parsed.cash !== 'object') { toast('Invalid cash structure', 'a-r'); return; }
      if (parsed.trades && !Array.isArray(parsed.trades)) { toast('Invalid trades structure', 'a-r'); return; }
      // Auto-backup before import. `markStateRestored()` comes FIRST: on a device whose local
      // writes are blocked (an unreadable copy — DATA-006) this snapshot is refused, silently,
      // on exactly the boot where it is the last thing standing between him and the old state.
      markStateRestored();
      idbSave({ ...S });
      // Selective merge: only overwrite known keys, don't delete existing ones not in file.
      // The list is DERIVED — see RESTORE_KEYS in core/state.js. The hand-written one that stood
      // here omitted fifteen keys and silently dropped portfolios, one-time income and bonus
      // quarters from every restore that got past the version check. (DATA-002)
      // Stripping on the way out opens a hole on the way back in: `S.settings = parsed.settings`
      // is a wholesale replace, so restoring a (correctly) key-less backup deleted the key the
      // owner had configured here, and the next AI call failed with "AI not configured" for no
      // visible reason. Put the local secrets back before the merge.
      preserveSecrets(S, parsed);
      RESTORE_KEYS.forEach(k => { if (k in parsed) S[k] = parsed[k]; });
      // MIGRATE THE FILE WHILE ITS VERSION IS STILL THE TRUTH.
      //
      // Migrations only ever ran inside load(). An imported file was installed raw, rendered
      // raw, and then `save()` published its version to the cloud. On the owner's own device the
      // next reload repaired it — but if anything had already seen a newer version (a second
      // device, or the debounced cloud save landing first), `mergeStates` keeps the higher
      // number and the SCHEMA FLOOR holds there, so every correction between the file's version
      // and the current one was skipped FOREVER, silently. Modelled on a v29 file against a v48
      // cloud copy: trades left at `broker: null` (so exits book gross proceeds), the
      // 7,539-unit Akbank TEJ lot absent, the IBKR sleeve un-rebuilt, `basisMonth` unset.
      //
      // The import path is the one place where a LOW version is a fact rather than a stale
      // reading, which is exactly what the floor cannot tell. Consuming it here, before anything
      // else can raise it, is the only moment that distinction still exists.
      // (audit 2026-08-13; prerequisite for raising the version ceiling — DATA-001)
      runMigrations();
      // DATA-011. Same door, same rule as `restoreBackup` — an imported row carries the FILE's
      // `_updatedAt`, and `save()` only stamps a row that has none.
      restampRestoredRows();
      save();
      flushCloudNow('import');
      renderAll();
      toast('Imported (' + parsed.holdings.length + ' holdings)', 'a-b');
    } catch (err) {
      toast('Import error: ' + err.message, 'a-r');
    }
  };
  r.readAsText(file);
}

export function exportCSV(tp) {
  let rows;
  const ms = getMs();
  if (tp === 'portfolio') {
    rows = [['Ticker', 'Type', 'SubType', 'Broker', 'AllocPct', 'Currency', ...ms.map(ml), 'TotalTRY', 'TotalUSD']];
    S.holdings.forEach(h =>
      rows.push([h.ticker, h.type, h.subType, h.broker, h.allocationPct, h.currency, ...ms.map(m => hMoTRY(h, m).toFixed(2)), hTotTRY(h).toFixed(2), hTotUSD(h).toFixed(2)])
    );
  } else if (tp === 'trades') {
    rows = [['Ticker', 'Type', 'Side', 'Entry', 'Qty', 'Lev', 'SL', 'TP1', 'TP2', 'Status']];
    S.trades.forEach(t => rows.push([t.ticker, t.type, t.side, t.entry, t.qty, t.leverage, t.sl, t.tp1, t.tp2, t.status]));
  } else {
    rows = [['Time', 'Action', 'Ticker', 'Details']];
    S.history.forEach(h => rows.push([h.ts, h.action, h.ticker, h.details]));
  }
  const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = 'zagx-' + tp + '.csv';
  a.click();
}

// === BACKUP REMINDER ===
export function daysSinceBackup() {
  if (!S.lastBackup) return Infinity;
  return (Date.now() - new Date(S.lastBackup).getTime()) / (1000 * 60 * 60 * 24);
}

export function checkBackup() {
  const d = daysSinceBackup(),
    lim = S.settings.backupDays || 7,
    el = document.getElementById('backupBanner');
  if (d >= lim) {
    const msg = S.lastBackup
      ? `Last backup was <strong>${Math.floor(d)} days ago</strong> (${ds(S.lastBackup)}). You should export a fresh backup.`
      : '<strong>No backup found.</strong> Export your data now to avoid losing everything if you clear browser cache.';
    el.innerHTML = `<div class="backup-banner"><div class="bb-msg"><span class="bb-icon">&#9888;</span><span>${msg}</span></div><div class="bb-actions"><button class="btn btn-p btn-s" data-action="exportAll">Backup Now</button><button class="btn btn-s" data-action="hideBackupBanner">Dismiss</button></div></div>`;
    el.style.display = 'block';
  } else {
    el.style.display = 'none';
  }
}

export function hideBackupBanner() {
  document.getElementById('backupBanner').style.display = 'none';
}

export function updateBackupInfo() {
  const el = document.getElementById('sLastBk'),
    ael = document.getElementById('sLastBkAgo');
  if (S.lastBackup) {
    el.textContent = ds(S.lastBackup);
    const d = Math.floor(daysSinceBackup());
    ael.textContent = d === 0 ? '(today)' : '(' + d + ' day' + (d > 1 ? 's' : '') + ' ago)';
    ael.style.color = d >= (S.settings.backupDays || 7) ? 'var(--red)' : 'var(--grn)';
  } else {
    el.textContent = 'Never';
    ael.textContent = '';
  }
}

// === AI SETTINGS ===

export function aiProviderChange() {
  const provider = document.getElementById('sAiProvider').value;
  const defaults = getProviderDefaults(provider);
  document.getElementById('sAiUrl').value = defaults.url;
  document.getElementById('sAiModel').value = defaults.model;
}

export function saveAISettings() {
  if (!S.settings.ai) S.settings.ai = {};
  S.settings.ai.provider = document.getElementById('sAiProvider').value;
  S.settings.ai.url = document.getElementById('sAiUrl').value.trim();
  S.settings.ai.apiKey = document.getElementById('sAiKey').value.trim();
  S.settings.ai.model = document.getElementById('sAiModel').value.trim();
  save();
  toast('AI settings saved', 'a-b');
}

export async function testAI() {
  const btn = document.getElementById('aiTestBtn');
  const status = document.getElementById('aiTestStatus');
  btn.disabled = true;
  btn.textContent = 'Testing...';
  status.textContent = '';

  // Temporarily apply form values without saving
  const origAi = { ...(S.settings.ai || {}) };
  S.settings.ai = {
    provider: document.getElementById('sAiProvider').value,
    url: document.getElementById('sAiUrl').value.trim(),
    apiKey: document.getElementById('sAiKey').value.trim(),
    model: document.getElementById('sAiModel').value.trim(),
  };

  const result = await testAIConnection();

  // Restore original settings (user must click Save to persist)
  S.settings.ai = origAi;

  btn.disabled = false;
  btn.textContent = 'Test Connection';

  if (result.ok) {
    status.innerHTML = `<span style="color:var(--grn)">✓ Connected — ${esc(result.model)} (${result.latencyMs}ms)</span>`;
  } else {
    status.innerHTML = `<span style="color:var(--red)">✗ ${esc(result.error)}</span>`;
  }
}

export function initAISettings() {
  const ai = S.settings.ai || {};
  const provEl = document.getElementById('sAiProvider');
  const urlEl = document.getElementById('sAiUrl');
  const keyEl = document.getElementById('sAiKey');
  const modelEl = document.getElementById('sAiModel');
  if (provEl) provEl.value = ai.provider || 'anthropic';
  if (urlEl) urlEl.value = ai.url || 'https://api.anthropic.com';
  if (keyEl) keyEl.value = ai.apiKey || '';
  if (modelEl) modelEl.value = ai.model || 'claude-sonnet-4-6';
}
